import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import Skeleton from "react-loading-skeleton";
import RTKQErrorCard from "../../../../../common/components/RTKQErrorCard";
import type {
  TransformedProduct,
  TransformedVariation,
} from "../../types/productApiTypes";
import type { SelectedAttributeSlugs } from "../../hooks/useProductVariationSelector";
import type { RTKQueryError } from "../../../../../common/components/RTKQErrorCard";
import ProductGallery from "./ProductGallery";
import VariationSelector from "./VariationSelector";
import ProductMeta from "../productSingle/ProductDetails/ProductMeta";

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: TransformedProduct | null | undefined;
  productLoading: boolean;
  productError: boolean;
  productErrorDetails: RTKQueryError | null;
  variationsLoading?: boolean;
  variationsError?: boolean;
  variationsErrorDetails?: RTKQueryError | null;
  selectedAttributeSlugs: SelectedAttributeSlugs;
  selectedVariation: TransformedVariation | null;
  handleAttributeSelect: (attributeName: string, valueSlug: string) => void;
}

const ProductModal: React.FC<ProductModalProps> = ({
  isOpen,
  onClose,
  product,
  productLoading,
  productError,
  productErrorDetails,
  variationsLoading,
  selectedAttributeSlugs,
  selectedVariation,
  handleAttributeSelect,
}) => {
  const { t } = useTranslation("common");
  const modalRef = useRef<HTMLDivElement>(null);

  const isVariable = product?.type === "variable";
  const shopUrl = window.themeVariables?.shopUrl || "/shop/";
  const isCombinationAvailable = selectedVariation !== null;

  const getSku = (): string => {
    if (isVariable && isCombinationAvailable && selectedVariation?.sku) {
      return selectedVariation.sku;
    }
    return product?.sku || "";
  };
  const currentSku = getSku();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const isLightboxOpen = !!document.querySelector(".yarl__container");
      if (isLightboxOpen) return;
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }
    return () => {
      document.body.style.overflow = "auto";
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const renderModalContent = () => {
    const isLoading =
      productLoading || (product?.type === "variable" && variationsLoading);

    if (isLoading) {
      return renderModalLoadingSkeleton();
    }
    if (productError) {
      return (
        <div className="modal-error">
          <RTKQErrorCard
            error={productErrorDetails}
            title={t("productModal.errorLoading")}
            compact={true}
          />
        </div>
      );
    }
    if (!product) {
      return <div className="modal-error">{t("productModal.notFound")}</div>;
    }

    return (
      <>
        <div className="modal-header">
          <h2 className="modal-title">{product.name}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="product-modal__content">
            <div className="modal-gallery">
              {product && (
                <ProductGallery
                  product={product}
                  selectedVariation={selectedVariation}
                  variationsLoading={variationsLoading}
                />
              )}
            </div>
            <div className="modal-details">
              {product.shortDescription && (
                <div
                  className="modal-description"
                  dangerouslySetInnerHTML={{ __html: product.shortDescription }}
                />
              )}

              {product.type === "variable" && (
                <VariationSelector
                  enrichedAttributes={product.enrichedAttributes || []}
                  selectedAttributes={selectedAttributeSlugs}
                  onAttributeSelect={handleAttributeSelect}
                  variationsLoading={variationsLoading}
                  containerClassName="modal-variations"
                  rowClassName="variation-select"
                />
              )}

              <div className="modal-meta-container">
                <ProductMeta
                  sku={currentSku}
                  categories={product.enrichedCategories || []}
                  tags={product.enrichedTags || []}
                  brands={product.enrichedBrands || []}
                  shopUrl={shopUrl}
                />
              </div>

              <div className="modal-details-actions">
                <a
                  href={product.permalink}
                  className="button view-full-details-btn"
                >
                  {t("productModal.viewFullDetails")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderModalLoadingSkeleton = () => (
    <>
      <div className="modal-header">
        <h2 className="modal-title">
          <Skeleton width={200} height={24} />
        </h2>
        <button className="modal-close" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="modal-body">
        <div className="product-modal__content">
          <div className="modal-gallery">
            <div className="product-gallery product-gallery-loading">
              <Skeleton height={400} className="gallery-skeleton" />
              <div
                className="thumbnails-skeleton"
                style={{ marginTop: "10px" }}
              >
                <Skeleton
                  height={80}
                  width={80}
                  count={4}
                  inline={true}
                  style={{ marginRight: "10px" }}
                />
              </div>
            </div>
          </div>
          <div className="modal-details">
            <div className="modal-description">
              <Skeleton count={2} />
            </div>
            <VariationSelector
              enrichedAttributes={[]}
              selectedAttributes={{}}
              onAttributeSelect={() => {}}
              variationsLoading={true}
              containerClassName="modal-variations"
              rowClassName="variation-select"
            />
            <div className="modal-meta-container">
              <Skeleton count={3} width="60%" height={18} />
            </div>
            <div className="modal-details-actions">
              <Skeleton height={42} width="100%" />
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(
    <div className="product-modal-overlay">
      <div className="product-modal" ref={modalRef}>
        {renderModalContent()}
      </div>
    </div>,
    document.body
  );
};

export default ProductModal;
